import type { CSSProperties } from 'react'
import type { CategoryTree, Maybe, MegaMenuFontStyle } from '@pylot-data/pylotschema'
import { isEspaciosCasaMenuItem } from './getMenuItemStableClassNames'

const MEGAMENU_FONT_PROPERTIES = new Set([
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'color',
    'background-color',
    'line-height',
    'letter-spacing',
    'text-align',
    'text-decoration',
    'text-transform'
])

const toCamelCase = (property: string): string =>
    property.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())

export const toMegaMenuCss = (
    styles?: Maybe<Array<Maybe<MegaMenuFontStyle>>> | null
): CSSProperties => {
    const css: Record<string, string> = {}
    for (const item of styles ?? []) {
        const property = item?.property?.trim()
        const value = item?.value?.trim()
        if (!property || !value || !MEGAMENU_FONT_PROPERTIES.has(property)) {
            continue
        }
        css[toCamelCase(property)] = value
    }
    return css as CSSProperties
}

/** Category Menu Image del admin. No usa Category Icon (`megamenu_icon`). */
export const getMegaMenuImage = (
    item?: Maybe<CategoryTree>
): string | undefined => {
    if (!item) return undefined
    if (item.megamenu_image) return item.megamenu_image
    return undefined
}

export const isEspaciosMegaMenu = (item?: Maybe<CategoryTree>): boolean => {
    if (!item) return false
    const type = item.megamenu_type?.trim().toLowerCase()
    if (
        type === 'espacios' ||
        type === 'espacio' ||
        type === 'espacio-de-la-casa' ||
        type === 'espacios-de-la-casa'
    ) {
        return true
    }
    return isEspaciosCasaMenuItem(item.url_key ?? undefined, item.name)
}

export const NOVEDADES_MENU_BLOCK = 'menu-novedades'

export const isNovedadesMenuItem = (
    item?: Maybe<CategoryTree>
): boolean => {
    if (!item) return false
    const name = (item.name || '').trim().toLowerCase()
    if (name === 'novedades') return true
    const key = (item.url_key || '').toLowerCase()
    return key === 'novedades'
}

export const findNovedadesCms = (
    item?: Maybe<CategoryTree>,
    siblings?: Maybe<Maybe<CategoryTree>>[]
): string => {
    const nodes = [item, ...(siblings ?? [])]
    for (const node of nodes) {
        if (!node) continue
        if (isNovedadesMenuItem(node) && node.cms_block?.content) {
            return node.cms_block.content
        }
        for (const child of node.children ?? []) {
            if (
                child &&
                isNovedadesMenuItem(child) &&
                child.cms_block?.content
            ) {
                return child.cms_block.content
            }
        }
    }
    return ''
}

export const ensureNovedadesFirst = (
    items: CategoryTree[],
    cmsHtml = ''
): CategoryTree[] => {
    const rest = items.filter((item) => !isNovedadesMenuItem(item))
    const existing = items.find(isNovedadesMenuItem)
    const content = existing?.cms_block?.content || cmsHtml || ''
    const novedades = {
        name: 'Novedades',
        url_key: 'novedades',
        url_path: 'novedades',
        include_in_menu: 1,
        position: 0,
        children: [],
        ...existing,
        name: existing?.name || 'Novedades',
        include_in_menu: 1,
        cms_block: {
            ...(existing?.cms_block || {}),
            content
        }
    } as CategoryTree
    return [novedades, ...rest]
}
